import React from 'react';
import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer';
import { renderResourceGroups } from 'v1/helpers/contactHelpers';
import { convertImageFileWithCloudinary } from 'lib/media/imageHelper';
import { get } from 'lodash';
import { Pin, Resource } from '__types__';
import styles from './ResourcePDFDocument.styles';

const MAX_GALLERY_ITEMS = 3;

interface ResourcePDFDocumentProps {
  resource: Resource;
  resourcePronouns?: string | null;
  fieldMappings: any;
  selectedFields: any;
  gallery: Pin[];
}

const ResourcePDFDocument: React.FC<ResourcePDFDocumentProps> = ({
  resource,
  resourcePronouns,
  gallery,
  fieldMappings,
  selectedFields
}) => {
  // NotoSansSC: Only register fonts that exist. Italic variants don't exist.
  Font.register({
    family: 'NotoSansSC',
    fonts: [
      {
        src: '/fonts/NotoSansSC-Thin.ttf',
        fontStyle: 'normal',
        fontWeight: 100
      },
      {
        src: '/fonts/NotoSansSC-ExtraLight.ttf',
        fontStyle: 'normal',
        fontWeight: 200
      },
      {
        src: '/fonts/NotoSansSC-Light.ttf',
        fontStyle: 'normal',
        fontWeight: 300
      },
      {
        src: '/fonts/NotoSansSC-Regular.ttf',
        fontStyle: 'normal',
        fontWeight: 400
      },
      {
        src: '/fonts/NotoSansSC-Medium.ttf',
        fontStyle: 'normal',
        fontWeight: 500
      },
      {
        src: '/fonts/NotoSansSC-SemiBold.ttf',
        fontStyle: 'normal',
        fontWeight: 600
      },
      {
        src: '/fonts/NotoSansSC-Bold.ttf',
        fontStyle: 'normal',
        fontWeight: 700
      },
      {
        src: '/fonts/NotoSansSC-ExtraBold.ttf',
        fontStyle: 'normal',
        fontWeight: 800
      },
      {
        src: '/fonts/NotoSansSC-Black.ttf',
        fontStyle: 'normal',
        fontWeight: 900
      }
    ]
  });

  function renderFieldItem(field, index) {
    const { getValue, getPrettyValue, label } = fieldMappings[field];
    const rawValue = getValue(resource);

    const prettyValue = getPrettyValue ? getPrettyValue(rawValue) : rawValue;
    return (
      <View key={index} style={styles.fieldItem}>
        <View style={styles.fieldItemLabel}>
          <Text>{label}</Text>
        </View>
        <View style={styles.fieldItemValue}>
          <Text>{prettyValue}</Text>
        </View>
      </View>
    );
  }
  function renderGalleryItem(pin) {
    const pinFile = get(pin, 'file', null);
    const pinResizedFileURL = convertImageFileWithCloudinary(pin.url, {
      width: 500,
      quality: 80
    });

    return (
      <View style={styles.galleryItem}>
        <Image
          src={pinFile && pinResizedFileURL ? pinResizedFileURL : pin.url}
          style={styles.galleryImage}
        />
      </View>
    );
  }

  return (
    <Document title={resource.full_name}>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <View style={styles.header}>
            <View style={styles.headerAvatarContainer}>
              {resource.profile_picture ? (
                <Image
                  src={convertImageFileWithCloudinary(
                    resource.profile_picture,
                    { width: 500, height: 500, quality: 80 }
                  )}
                  style={styles.headerAvatarImage}
                />
              ) : (
                <View style={styles.headerAvatarImagePlaceholder} />
              )}
            </View>
            <View style={styles.headerContentContainer}>
              <Text style={styles.heading}>
                {resource.full_name}
                {resourcePronouns ? (
                  <Text style={styles.headingPronouns}>
                    {' '}
                    {resourcePronouns}
                  </Text>
                ) : null}
              </Text>
              {renderResourceGroups(resource.groups) && (
                <Text>{renderResourceGroups(resource.groups)}</Text>
              )}
            </View>
          </View>
        </View>
        <View style={styles.section}>
          {selectedFields.map((field, index) => renderFieldItem(field, index))}
        </View>
        {gallery && gallery.length > 1 && (
          <View style={styles.section}>
            <View style={styles.galleryContainer}>
              {gallery.map((item, index) => {
                if (index + 1 > MAX_GALLERY_ITEMS) return null;
                return renderGalleryItem(item);
              })}
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
};

export default ResourcePDFDocument;
